// C:\Users\phill\paperplay\frontend\src\features\accountsettings\subscriptions\subscriptionTypes.tsx

// C:\Users\phill\paperplay\frontend\src\features\accountsettings\subscriptions\subscriptionTypes.tsx

import type { FeePolicy } from '../../../lib/pricing';

// ──────────────────────────────────────────────────────────────────────────────
// Plan & Status types
// ──────────────────────────────────────────────────────────────────────────────
export type PlanId = string;

/**
 * Stripe/Backend status (kept broad for future-proofing).
 * Add extra literals here if your backend emits more values.
 */
export type SubStatus = 'active' | 'trialing' | 'canceled' | 'past_due' | 'incomplete' | 'unpaid';

export interface Plan {
  id: PlanId;
  label: string;
  minShows: number;
  maxShows: number | null;
  priceCents: number;
  currency: string;
}

// ──────────────────────────────────────────────────────────────────────────────
/** Raw subscription payload as returned by your backend. */
export interface SubscriptionData {
  plan_id?: PlanId;
  status?: SubStatus;

  // Usage / cycle info
  showsMtd?: number;

  // Window anchors (ISO timestamps)
  billingAnchor?: string;
  currentPeriodStart?: string;
  currentPeriodEnd?: string;

  // Plan snapshots the backend may include
  currentPlan?: Plan;
  nextPlan?: Plan | null;

  // Flags
  autoUpgradedThisCycle?: boolean;
  pendingDowngradePlanId?: PlanId | null;
  pendingEffectiveAt?: string | null;
  feePolicy?: FeePolicy;

  // Optional org entitlements (if present on your API)
  entitlements?: {
    periodStart: string;
    periodEnd: string;
    payouts: { used: number; included: number };
    forms1099: { used: number; included: number };
  };

  // Optional Stripe refs often displayed in UI
  stripeCustomerId?: string;
  stripeSubscriptionId?: string;

  // Optional fee disclosure policy (if present on your API)
  requires_disclosure_acceptance?: boolean;
  disclosure_version?: string;
  disclosure_accepted?: boolean;
  card_surcharge_enabled?: boolean;
  card_surcharge_reason?: string | null;

  updatedAt?: string;
}

// ──────────────────────────────────────────────────────────────────────────────
/** UI-friendly snapshot derived from SubscriptionData + catalog. */
export interface NormalizedSubscription {
  plan?: Plan;
  nextPlan?: Plan | null;
  currentPeriodStart?: string;
  currentPeriodEnd?: string;
  pendingEffectiveAt?: string | null;
  feePolicy?: FeePolicy;
  usage?: {
    shows?: { used?: number };
  };
  showsMtd?: number;
}

// ──────────────────────────────────────────────────────────────────────────────
/** Redux slice state for the subscription feature. */
export interface SubscriptionState {
  subscription: SubscriptionData | null;
  normalized: NormalizedSubscription | null; // ← consumed by the UI
  plans: Plan[];
  loading: boolean;
  error: string | null;
  applyingPromo: boolean;
}

// ──────────────────────────────────────────────────────────────────────────────
/**
 * Produce a UI-friendly snapshot from the raw subscription and the plan catalog.
 * – Prefer `currentPlan` from the payload, otherwise resolve by `plan_id`.
 * – Copy through `nextPlan`, `currentPeriodEnd`, and a simple shows usage view.
 */
export function normalizeSubscription(
  sub: SubscriptionData,
  plans: Plan[],
): NormalizedSubscription {
  const resolvedPlan =
    sub.currentPlan ?? (sub.plan_id ? plans.find(p => p.id === sub.plan_id) : undefined);

  return {
    plan: resolvedPlan,
    nextPlan: sub.nextPlan ?? null,
    currentPeriodStart: sub.currentPeriodStart,
    currentPeriodEnd: sub.currentPeriodEnd,
    pendingEffectiveAt: sub.pendingEffectiveAt ?? null,
    feePolicy: sub.feePolicy,
    usage: { shows: { used: sub.showsMtd } },
    showsMtd: sub.showsMtd,
  };
}
